import { createRoute, Link, useLocation, useNavigate, useParams } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { appRoute } from "./route";
import { createSignal, For, Show } from "solid-js";
import { NotFoundState } from "@/components/ui/not-found-state";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageContainer,
  Separator,
  Skeleton,
  TextField,
} from "@fleet/ui";
import { ArrowLeft, Check, Link2, Loader2, Mail, UserPlus, Users } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { canonicalUrl } from "@/lib/canonical-link";
import { useOrganizationMembers } from "@/features/organizations/hooks/use-organization-members";
import { api, createApiQuery } from "@/lib/api";
import { type components, queryKeys } from "@cashsync/shared";
import { useI18n } from "@/providers/i18n-provider";
import { OrganizationMemberRow } from "@/features/organizations/components/organization-member-row";
import { safeRedirectTarget, useSession } from "@/lib/auth";

function OrganizationDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const params = useParams({ from: "/_app/organizations/$orgId" });
  const [email, setEmail] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [copied, setCopied] = createSignal(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl());
      setCopied(true);
      showToast(t(msg`Link copied`), "success");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast(t(msg`Couldn't copy the link`), "error");
    }
  };

  // Organizations are server data: a visitor cannot know whether
  // an id exists (the API requires a session), so it is not even called — avoids 401 and
  // the generic error toast. Just like `does not exist`, a visitor never
  // has access to ANY org, so both cases share NotFoundState
  // (previously an `AuthPlaceholder` covered EVERYTHING behind a login wall,
  // including non-existent ids — indistinguishable from a broken deep link).
  const orgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
    () => ({ enabled: session.isAuthenticated() }),
  );

  // P23 / CC-60 · I2: `.data` only on `isSuccess`.
  const org = () => (orgs.isSuccess ? orgs.data?.find((o) => o.id === params().orgId) : undefined);

  const { members, isLoading, inviteMember, removeMember } = useOrganizationMembers(
    () => params().orgId,
  );

  const handleInvite = async (e: Event) => {
    e.preventDefault();
    const mail = email().trim();
    if (!mail) {
      return;
    }

    setIsSubmitting(true);
    try {
      await inviteMember(mail, "member");
      setEmail("");
      showToast(t(msg`Invitation sent`), "success");
    } catch (err) {
      console.error("Error inviting member:", err);
      showToast(t(msg`Error sending invitation`), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (memberId: string) => {
    try {
      await removeMember(memberId);
      showToast(t(msg`Member removed`), "success");
    } catch (err) {
      console.error("Error removing member:", err);
      showToast(t(msg`Error removing member`), "error");
    }
  };

  return (
    <PageContainer maxWidth="5xl" class="space-y-8">
      <Show
        when={session.isAuthenticated()}
        fallback={
          // Visitor: the orgs API requires a session and is NOT called (above,
          // `enabled: session.isAuthenticated()`), so it doesn't even
          // attempt to resolve whether the id exists — a visitor never has
          // access to ANY org, whether it exists or not. Previously this was behind
          // an `AuthPlaceholder` that covered the entire screen with a login wall,
          // indistinguishable from a broken deep link.
          <NotFoundState
            variant="no-access"
            title={t(msg`Organization not found`)}
            description={t(
              msg`This organization doesn't exist, or you need to sign in to view it.`,
            )}
            primaryAction={{
              label: t(msg`Sign in now`),
              onClick: () => {
                void navigate({
                  to: "/login",
                  search: { redirect: safeRedirectTarget(location().pathname) },
                });
              },
            }}
            secondaryAction={{
              label: t(msg`Back to Organizations`),
              onClick: () => {
                void navigate({ to: "/organizations" });
              },
            }}
          />
        }
      >
        <Show
          when={!orgs.isLoading}
          fallback={
            <div class="space-y-8">
              <div class="flex items-center gap-4">
                <Skeleton class="h-8 w-8 rounded-md" />
                <Skeleton class="h-8 w-48" />
              </div>
              <div class="grid gap-6 md:grid-cols-2">
                <Skeleton class="h-64 w-full rounded-xl" />
                <Skeleton class="h-64 w-full rounded-xl" />
              </div>
            </div>
          }
        >
          <Show
            when={org()}
            fallback={
              <NotFoundState
                variant={orgs.isError ? "offline" : "no-access"}
                title={orgs.isError ? undefined : t(msg`Organization not found`)}
                description={
                  orgs.isError
                    ? undefined
                    : t(
                        msg`This organization doesn't exist, or you're not a member and don't have access to it.`,
                      )
                }
                primaryAction={{
                  label: t(msg`Back to Organizations`),
                  onClick: () => {
                    void navigate({ to: "/organizations" });
                  },
                }}
              />
            }
          >
            {(o) => (
              <>
                <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div class="space-y-1">
                    <div class="flex items-center gap-3">
                      <Link to="/organizations">
                        <Button variant="ghost" size="icon" class="h-8 w-8">
                          <ArrowLeft class="h-4 w-4" />
                        </Button>
                      </Link>
                      <h1 class="text-3xl font-bold tracking-tight">{o().name}</h1>
                    </div>
                    <div class="flex items-center gap-2 pl-11">
                      <Badge variant="secondary" class="text-[10px] uppercase tracking-wider">
                        @{o().slug}
                      </Badge>
                      <span class="text-xs text-muted-foreground">
                        {(() => {
                          const orgId = o().id;
                          return t(msg`ID: ${orgId}`);
                        })()}
                      </span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    class="flex items-center gap-2 self-start sm:self-auto"
                    data-testid="copy-link-button"
                  >
                    <Show when={copied()} fallback={<Link2 class="h-4 w-4" />}>
                      <Check class="h-4 w-4 text-emerald-500" />
                    </Show>
                    <span>{copied() ? t(msg`Link copied`) : t(msg`Copy link`)}</span>
                  </Button>
                </header>

                <div class="grid gap-6 md:grid-cols-2">
                  {/* Miembros */}
                  <Card>
                    <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle class="text-lg font-bold">{t(msg`Members`)}</CardTitle>
                      <Users class="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div class="space-y-4">
                        <For
                          each={members()}
                          fallback={
                            <Show when={!isLoading()}>
                              <div class="py-10 text-center text-sm text-muted-foreground">
                                {t(msg`No members yet`)}
                              </div>
                            </Show>
                          }
                        >
                          {(member) => (
                            <OrganizationMemberRow member={member} onRemove={handleRemove} />
                          )}
                        </For>
                        <Show when={isLoading() && members().length === 0}>
                          <For each={Array.from({ length: 3 })}>
                            {() => <Skeleton class="h-12 w-full rounded-md" />}
                          </For>
                        </Show>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Invitaciones / Acciones */}
                  <Card>
                    <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle class="text-lg font-bold">{t(msg`Invite members`)}</CardTitle>
                      <Mail class="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent class="space-y-6">
                      <form onSubmit={handleInvite} class="space-y-4">
                        <div class="space-y-2">
                          <TextField
                            type="email"
                            placeholder={t(msg`email@example.com`)}
                            value={email()}
                            onInput={(e) => setEmail(e.currentTarget.value)}
                            disabled={isSubmitting()}
                          />
                        </div>
                        <Button
                          type="submit"
                          class="w-full gap-2"
                          disabled={isSubmitting() || !email().includes("@")}
                        >
                          <Show
                            when={isSubmitting()}
                            fallback={
                              <>
                                <UserPlus class="h-4 w-4" /> {t(msg`Invite`)}
                              </>
                            }
                          >
                            <Loader2 class="h-4 w-4 animate-spin" /> {t(msg`Inviting...`)}
                          </Show>
                        </Button>
                      </form>

                      <Separator />

                      <div class="space-y-2 text-center">
                        <p class="text-xs text-muted-foreground leading-relaxed">
                          {t(msg`Invited members will receive an email to join this organization.`)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </Show>
        </Show>
      </Show>
    </PageContainer>
  );
}

export const organizationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organizations/$orgId",
  component: OrganizationDetailPage,
});
